import type { Dispatch, FC, SetStateAction } from "react";

import { Check, Translate } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { getReportLanguageDisplayName } from "@/common/constants/languages";

import styles from "./SurveyGroupMembersPage.module.scss";

export interface GroupDetailsTabProps {
  onNavigateBack: () => void;
  detailsName: string;
  onDetailsNameChange: (value: string) => void;
  detailsSortOrder: string;
  onDetailsSortOrderChange: (value: string) => void;
  detailsHidden: boolean;
  onDetailsHiddenChange: (hidden: boolean) => void;
  nonEnglishLanguages: string[];
  translatedCount: number;
  translatedNames: Record<string, string>;
  onTranslatedNamesChange: Dispatch<SetStateAction<Record<string, string>>>;
  selectedLang: string;
  onSelectedLangChange: (value: string) => void;
  translatingLang: boolean;
  translatingAll: boolean;
  onTranslateLang: () => void;
  onTranslateAll: () => void;
  onSaveDetails: () => void;
  savingDetails: boolean;
}

const GroupDetailsTab: FC<GroupDetailsTabProps> = ({
  onNavigateBack,
  detailsName,
  onDetailsNameChange,
  detailsSortOrder,
  onDetailsSortOrderChange,
  detailsHidden,
  onDetailsHiddenChange,
  nonEnglishLanguages,
  translatedCount,
  translatedNames,
  onTranslatedNamesChange,
  selectedLang,
  onSelectedLangChange,
  translatingLang,
  translatingAll,
  onTranslateLang,
  onTranslateAll,
  onSaveDetails,
  savingDetails,
}) => {
  return (
    <Stack spacing={2}>
      <Paper variant="outlined" className={styles.sectionPaper}>
        <Typography variant="subtitle2" className={styles.sectionTitle}>
          Group Settings
        </Typography>
        <Stack spacing={2.5} className={styles.formStack}>
          <TextField
            label="Group Name"
            variant="outlined"
            value={detailsName}
            onChange={(e) => onDetailsNameChange(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Sort Order"
            variant="outlined"
            value={detailsSortOrder}
            onChange={(e) => onDetailsSortOrderChange(e.target.value)}
            type="number"
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={detailsHidden}
                onChange={(e) => onDetailsHiddenChange(e.target.checked)}
              />
            }
            label="Hidden from public"
          />
        </Stack>
      </Paper>

      {nonEnglishLanguages.length > 0 && (
        <Paper variant="outlined" className={styles.sectionPaper}>
          <Box className={styles.translatedToolbar}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="subtitle2">Translated Names</Typography>
              <Chip
                label={`${translatedCount} / ${nonEnglishLanguages.length}`}
                size="small"
                color={
                  translatedCount === nonEnglishLanguages.length
                    ? "success"
                    : "default"
                }
                variant="outlined"
              />
            </Stack>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                translatingAll ? <CircularProgress size={16} /> : <Translate />
              }
              onClick={onTranslateAll}
              disabled={
                translatingAll || translatingLang || !detailsName.trim()
              }
            >
              {translatingAll ? "Translating..." : "Translate All"}
            </Button>
          </Box>

          <Stack spacing={2} className={styles.formStack}>
            <TextField
              select
              label="Language"
              variant="outlined"
              value={selectedLang}
              onChange={(e) => onSelectedLangChange(e.target.value)}
              fullWidth
            >
              {nonEnglishLanguages.map((code) => {
                const hasTranslation = !!translatedNames[code]?.trim();
                return (
                  <MenuItem key={code} value={code}>
                    <Box className={styles.menuItemRow}>
                      <span>{getReportLanguageDisplayName(code)}</span>
                      {hasTranslation && (
                        <Check
                          fontSize="small"
                          color="success"
                          className={styles.menuItemCheck}
                        />
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>
            {selectedLang && (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label={`Name in ${getReportLanguageDisplayName(selectedLang)}`}
                  variant="outlined"
                  value={translatedNames[selectedLang] ?? ""}
                  onChange={(e) =>
                    onTranslatedNamesChange((prev) => ({
                      ...prev,
                      [selectedLang]: e.target.value,
                    }))
                  }
                  fullWidth
                />
                <Tooltip title="Auto-translate from English name">
                  <span>
                    <IconButton
                      onClick={onTranslateLang}
                      disabled={
                        translatingLang || translatingAll || !detailsName.trim()
                      }
                      color="primary"
                    >
                      {translatingLang ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Translate />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      <Box className={styles.detailsActions}>
        <Button onClick={() => onNavigateBack()}>Cancel</Button>
        <Button
          onClick={onSaveDetails}
          variant="contained"
          disabled={savingDetails || !detailsName.trim()}
        >
          Save Details
        </Button>
      </Box>
    </Stack>
  );
};

export default GroupDetailsTab;
